import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Store, DollarSign, Loader2, CheckCircle2, AlertTriangle, ExternalLink, ArrowRight, BadgeCheck, Wallet, ShoppingBag, TrendingUp, Package, Check } from "lucide-react";
import api from "@/lib/api";

const KIT_PREVIEW = [
  { label: "Pictures", img: "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/39c36c2a339ff56c8bf3404977e8dcbf1c5a1b123892ec71b9cf4be4a85d4a76.jpeg" },
  { label: "Keynote", img: "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/06b4651d19f88b9996225fda9e7f3fa0669884c564fe331cfc0a0eff1101053c.jpeg" },
  { label: "PDF", img: "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/3e156afc07bc8371f47dc1889d631d524b3f05fe1f6e075d7087d751222b18a4.jpeg" },
  { label: "Worksheet", img: "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/6e505a9ee969c7c341b4589ce6921d3a9d153a9f34b809b2d4830b9b66b2f541.jpeg" },
];

const Badge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold border ${ok ? "bg-[#5d8a52]/15 border-[#5d8a52] text-[#8fbf82]" : "bg-[#c14b4b]/15 border-[#c14b4b] text-[#f0736f]"}`}>
    {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {label}
  </span>
);

const Stat = ({ label, value, tid }) => (
  <div className="rounded-2xl border-2 border-[#5d8a52] bg-black p-4 text-center" style={{ boxShadow: "0 0 22px -10px rgba(93,138,82,.5)" }}>
    <div className="text-[10px] font-bold text-white/50 uppercase tracking-wide">{label}</div>
    <div className="text-3xl font-extrabold text-white mt-0.5 tabular-nums" data-testid={tid}>{value}</div>
  </div>
);

export default function Seller() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [bundlePrice, setBundlePrice] = useState(null);
  const [included, setIncluded] = useState([]);
  const [priceOpts, setPriceOpts] = useState([2.99, 3.99, 4.99, 5.99, 6.99, 7.99]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [needsConnect, setNeedsConnect] = useState(false);
  const [chartWin, setChartWin] = useState("7d");
  const [series, setSeries] = useState(null);
  const [seriesBusy, setSeriesBusy] = useState(false);

  const loadSeries = useCallback(async (win) => {
    setSeriesBusy(true);
    try {
      const { data } = await api.get("/seller/earnings-series", { params: { window: win } });
      setSeries(data);
    } catch { /* silent */ } finally { setSeriesBusy(false); }
  }, []);

  useEffect(() => { loadSeries(chartWin); }, [chartWin, loadSeries]);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get("/seller/status"), api.get("/seller/pricing")]);
      setStatus(s.data);
      setBundlePrice(p.data.bundle_price ?? null);
      setIncluded(p.data.included || []);
      if (p.data.options?.length) setPriceOpts(p.data.options);
    } catch {
      toast.error("Couldn't load your seller status.");
    } finally {
      setLoading(false);
    }
  }, []);

  const saveBundle = async (value) => {
    const price = value === "" ? null : parseFloat(value);
    setSaving(true);
    setBundlePrice(price);
    try {
      const { data } = await api.post("/seller/pricing", { price });
      setBundlePrice(data.bundle_price ?? null);
      toast.success(price ? `Your full kit now sells for $${price.toFixed(2)} on every video.` : "Your kit is no longer for sale.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save price.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("setup") === "complete") {
      toast.success("Welcome back! Refreshing your payout status…");
      setParams({}, { replace: true });
      setTimeout(load, 400);
    }
  }, [params, setParams, load]);

  const startOnboarding = async () => {
    setBusy(true);
    setNeedsConnect(false);
    try {
      const { data } = await api.post("/seller/onboard", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail === "connect_not_enabled") {
        setNeedsConnect(true);
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't start payout setup.");
      }
    } finally {
      setBusy(false);
    }
  };

  const stats = status?.stats || {};
  const onboarded = status?.onboarded;
  const feePct = stats.platform_fee_pct ?? 30;
  const earns = bundlePrice ? (bundlePrice * (100 - feePct) / 100) : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-2xl bg-black border-2 border-[#5d8a52] text-[#8fbf82] flex items-center justify-center" style={{ boxShadow: "0 0 18px -3px rgba(143,191,130,.6)" }}>
            <Store size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-white">Sell &amp; Earn</h1>
            <p className="text-sm text-white/70">Sell your full creator kit and earn on every video.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/60"><Loader2 size={22} className="animate-spin" /></div>
        ) : (
          <div className="mt-5 space-y-4">
            {/* How it works / terms */}
            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-5" data-testid="seller-terms-card">
              <div className="flex items-center gap-2 font-extrabold text-white"><DollarSign size={18} className="text-[#8fbf82]" /> How selling works</div>
              <ul className="mt-2.5 space-y-1.5 text-sm text-white">
                <li className="flex gap-2"><ArrowRight size={15} className="text-[#5d8a52] mt-0.5 shrink-0" /> <span>Buyers get your <b>whole kit in one purchase</b> — no fiddly per-file prices.</span></li>
                <li className="flex gap-2"><ArrowRight size={15} className="text-[#5d8a52] mt-0.5 shrink-0" /> Pick one price below — it applies to the kit on <b>all</b> your videos.</li>
                <li className="flex gap-2"><ArrowRight size={15} className="text-[#5d8a52] mt-0.5 shrink-0" /> <span>You keep <b>{100 - feePct}%</b> of your set price &amp; Vittyyo keeps a <b>{feePct}%</b> commission. Vittyyo also covers the card processing &amp; transaction fees.</span></li>
                <li className="flex gap-2"><ArrowRight size={15} className="text-[#5d8a52] mt-0.5 shrink-0" /> Stripe handles verification, taxes and payouts straight to your bank.</li>
              </ul>
              <p className="text-xs text-white/60 mt-3">Currently in test mode — no real charges happen until Vittyyo enables live selling.</p>
            </div>

            {/* Bundle price */}
            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-5" data-testid="seller-pricing-card">
              <div className="flex items-center gap-2 font-extrabold text-white"><Package size={18} className="text-[#8fbf82]" /> Your kit &amp; price</div>

              {/* Preview strip */}
              <div className="mt-3 grid grid-cols-4 gap-2" data-testid="kit-preview-strip">
                {KIT_PREVIEW.map((p) => (
                  <div key={p.label} className="rounded-xl border-2 border-[#3f5a38] overflow-hidden bg-black">
                    <div className="aspect-square overflow-hidden"><img src={p.img} alt={p.label} className="w-full h-full object-cover" loading="lazy" /></div>
                    <div className="text-[10px] font-bold text-center text-[#8fbf82] py-1">{p.label}</div>
                  </div>
                ))}
              </div>

              {/* What's included */}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1" data-testid="kit-included">
                {included.map((it) => (
                  <span key={it.asset} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#8fbf82]">
                    <Check size={13} className="text-[#5d8a52]" /> {it.label}
                  </span>
                ))}
              </div>

              {/* Price picker */}
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="text-sm font-extrabold text-white shrink-0">Sell the full kit for</label>
                <div className="relative flex-1">
                  <select
                    data-testid="bundle-price-select"
                    value={bundlePrice ? String(bundlePrice) : ""}
                    onChange={(e) => saveBundle(e.target.value)}
                    disabled={saving}
                    className="w-full appearance-none text-center font-extrabold text-base rounded-full border-2 border-[#5d8a52] bg-black text-white px-3 py-2.5 cursor-pointer disabled:opacity-60"
                  >
                    <option value="">Not selling yet</option>
                    {priceOpts.map((o) => <option key={o} value={String(o)}>${o.toFixed(2)}</option>)}
                  </select>
                  {saving && <Loader2 size={16} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#8fbf82]" />}
                </div>
              </div>
              {bundlePrice ? (
                <p className="text-[13px] text-[#8fbf82] font-semibold mt-2.5" data-testid="earnings-preview">
                  You earn <b>${earns.toFixed(2)}</b> per sale · Vittyyo keeps ${(bundlePrice - earns).toFixed(2)}.
                </p>
              ) : (
                <p className="text-[11px] text-white/60 mt-2.5">Pick a price to start selling. You can change it anytime — it updates the kit on every one of your videos at once.</p>
              )}
            </div>

            {/* Payout setup / status */}
            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-5" data-testid="seller-payout-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-extrabold text-white"><Wallet size={18} className="text-[#8fbf82]" /> Payout account</div>
                {onboarded && <Badge ok label="Ready to sell" />}
              </div>

              {onboarded ? (
                <div className="mt-3" data-testid="seller-status-badges">
                  <div className="flex flex-wrap gap-2">
                    <Badge ok={status.details_submitted} label="Details submitted" />
                    <Badge ok={status.charges_enabled} label="Charges enabled" />
                    <Badge ok={status.payouts_enabled} label="Payouts enabled" />
                  </div>
                  <p className="text-xs text-white/60 mt-3 leading-relaxed" data-testid="seller-payout-note">
                    Tips and kit sales go <b>straight into your Stripe account</b> — Vittyyo never holds your money.
                    Stripe pays out to your bank automatically on its regular schedule.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-white/60 mt-2">
                    Set up your payout account once to start selling. Stripe securely collects your identity and
                    bank details — it takes a couple of minutes.
                  </p>

                  {needsConnect ? (
                    <div className="mt-3 rounded-xl border-2 border-[#5a4a1e] bg-[#241f12] p-3.5" data-testid="connect-not-enabled">
                      <div className="flex items-center gap-2 font-bold text-[#f0c975] text-sm"><AlertTriangle size={15} /> One-time setup needed by the platform owner</div>
                      <p className="text-xs text-white/70 mt-1.5">
                        Stripe Connect isn't switched on for the marketplace yet. The Vittyyo owner needs to enable
                        Connect in the Stripe dashboard, then this button will work for everyone.
                      </p>
                      <a href="https://dashboard.stripe.com/connect/overview" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-bold text-[#8fbf82] hover:underline" data-testid="enable-connect-link">
                        Enable Stripe Connect <ExternalLink size={13} />
                      </a>
                    </div>
                  ) : null}

                  <button
                    data-testid="setup-payouts-btn"
                    onClick={startOnboarding}
                    disabled={busy}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl bg-[#4f7746] text-white hover:bg-[#3f6238] transition-colors disabled:opacity-60"
                  >
                    {busy ? <><Loader2 size={17} className="animate-spin" /> Starting…</> : <><BadgeCheck size={17} /> Set Up Payouts</>}
                  </button>
                </>
              )}
            </div>

            {/* Earnings */}
            <div className="grid grid-cols-3 gap-3" data-testid="seller-earnings">
              <Stat label="Your earnings" value={`$${(stats.earnings_usd ?? 0).toFixed(2)}`} tid="stat-earnings" />
              <Stat label="Gross sales" value={`$${(stats.gross_usd ?? 0).toFixed(2)}`} tid="stat-gross" />
              <Stat label="Kits sold" value={stats.sales_count ?? 0} tid="stat-sales" />
            </div>

            {/* Earnings chart */}
            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-5" data-testid="earnings-chart-card" style={{ boxShadow: "0 0 30px -14px rgba(93,138,82,.55)" }}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 font-extrabold text-white"><TrendingUp size={18} className="text-[#8fbf82]" /> Earnings</div>
                <div className="flex items-center gap-1 rounded-full bg-black border border-[#3f5a38] p-0.5">
                  {[["1D", "1d"], ["7D", "7d"], ["30D", "30d"], ["YTD", "ytd"]].map(([l, w]) => (
                    <button
                      key={w}
                      data-testid={`earnings-win-${w}`}
                      onClick={() => setChartWin(w)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${chartWin === w ? "bg-[#4f7746] text-white" : "text-white/50 hover:text-white"}`}
                    >{l}</button>
                  ))}
                </div>
              </div>

              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-[#8fbf82] tabular-nums" data-testid="earnings-window-total">${(series?.total ?? 0).toFixed(2)}</span>
                <span className="text-xs font-semibold text-white/45">earned this period</span>
                {seriesBusy && <Loader2 size={14} className="animate-spin text-[#8fbf82]" />}
              </div>

              {(() => {
                const pts = series?.points || [];
                const max = Math.max(0.01, ...pts.map((p) => p.value));
                const hasAny = pts.some((p) => p.value > 0);
                return (
                  <>
                    <div className="mt-4 flex items-end gap-1 h-32" data-testid="earnings-bars">
                      {pts.map((p, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                          <div className="w-full rounded-t-md transition-all duration-300"
                            style={{
                              height: `${Math.max(p.value > 0 ? 6 : 2, (p.value / max) * 100)}%`,
                              background: p.value > 0 ? "linear-gradient(180deg,#8fbf82,#4f7746)" : "#1c1c24",
                              boxShadow: p.value > 0 ? "0 0 12px -2px rgba(143,191,130,.7)" : "none",
                            }}
                            title={`${p.label}: $${p.value.toFixed(2)}`}
                          />
                          <div className="absolute -top-6 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-bold text-[#8fbf82] bg-black px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">
                            ${p.value.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1.5 flex gap-1">
                      {pts.map((p, i) => {
                        const step = chartWin === "1d" ? 4 : chartWin === "30d" ? 5 : 1;
                        return (
                          <div key={i} className="flex-1 text-center text-[9px] font-semibold text-white/40 truncate">
                            {i % step === 0 ? p.label : ""}
                          </div>
                        );
                      })}
                    </div>
                    {!hasAny && !seriesBusy && (
                      <div className="text-center text-xs text-white/45 mt-3">No earnings in this period yet.</div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-5">
              <div className="flex items-center gap-2 font-extrabold text-white mb-2"><ShoppingBag size={18} className="text-[#8fbf82]" /> Recent sales</div>
              {status?.sales?.length ? (
                <div className="divide-y divide-white/10" data-testid="seller-sales-list">
                  {status.sales.map((s) => (
                    <div key={s.id} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-white font-semibold truncate">{s.manual_title || "Video"}</span>
                      <span className="text-[#8fbf82] font-bold tabular-nums">+${Number(s.seller_earnings || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-white/60 py-4">
                  <TrendingUp size={16} /> No sales yet — set a kit price and share your videos to start earning.
                </div>
              )}
            </div>

            <button onClick={() => navigate("/my-videos")} className="text-sm font-semibold text-[#8fbf82] hover:underline" data-testid="seller-back-videos">
              ← Back to My Videos
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
